import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch, Link } from 'react-router-dom'
import {
  Button,
  Table,
  Tag,
  Space,
  Empty,
  Select,
  Input,
  Switch,
  Modal
} from 'antd'
import { ReloadOutlined, ShopOutlined } from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import { getRequest, formatUrl, dutyCustomersUrl } from '../../../config'
import { formatMoney } from '../../Analytics/utils'
import IdentityLabel from '../../../components/IdentityLabel'
import CustomerDetailBody from './CustomerDetailBody'
import CustomerDetail from './Detail'

const ORDER_OPTIONS = [
  { value: '-last_activity', label: 'Most recently active' },
  { value: '-request_count', label: 'Most duty requests' },
  { value: '-completed_count', label: 'Most completed' },
  { value: '-paid_count', label: 'Most paid' },
  { value: '-revenue', label: 'Highest revenue' },
  { value: '-created_at', label: 'Newest' }
]

// Build the querystring from the active filters, omitting blanks. `search` matches phone/momo
// name/number; `is_dealer` limits to registered dealers; `ordering` sorts by an activity column.
const buildQuery = ({ search, dealersOnly, ordering }) => {
  const params = new URLSearchParams()
  if (search) params.append('search', search)
  if (dealersOnly) params.append('is_dealer', 'true')
  if (ordering) params.append('ordering', ordering)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

const CustomersList = props => {
  const [loading, setLoading] = useState(false)
  const [customers, setCustomers] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [search, setSearch] = useState('')
  const [dealersOnly, setDealersOnly] = useState(false)
  const [ordering, setOrdering] = useState('-last_activity')
  const [quickViewId, setQuickViewId] = useState(null)

  const getCustomers = async (
    filters = { search, dealersOnly, ordering },
    url = null
  ) => {
    const response = await getRequest(
      url ? formatUrl(url) : `${dutyCustomersUrl}${buildQuery(filters)}`,
      'customers',
      setLoading
    )
    if (response.success) {
      setCustomers(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getCustomers({ search: '', dealersOnly: false, ordering: '-last_activity' })
    // eslint-disable-next-line
  }, [])

  const applyFilters = () => getCustomers({ search, dealersOnly, ordering })

  const clearFilters = () => {
    setSearch('')
    setDealersOnly(false)
    setOrdering('-last_activity')
    getCustomers({ search: '', dealersOnly: false, ordering: '-last_activity' })
  }

  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <IdentityLabel value={r} strong />
            {r.isRegisteredDealer ? (
              <Tag icon={<ShopOutlined />} color="green" style={{ marginRight: 0 }}>
                Dealer
              </Tag>
            ) : null}
          </Space>
          {r.momoName ? (
            <span style={{ color: '#888', fontSize: 12 }}>{r.momoName}</span>
          ) : null}
        </Space>
      )
    },
    { title: 'Requests', dataIndex: 'requestCount', key: 'requestCount' },
    { title: 'Completed', dataIndex: 'completedCount', key: 'completedCount' },
    { title: 'Paid', dataIndex: 'paidCount', key: 'paidCount' },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: v => formatMoney(v, 'GHS')
    },
    {
      title: 'Chats',
      dataIndex: 'conversationCount',
      key: 'conversationCount'
    },
    {
      title: 'Last active',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: v => (v ? new Date(v).toLocaleString() : '—')
    },
    {
      title: 'First seen',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: v => (v ? new Date(v).toLocaleDateString() : '—')
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => setQuickViewId(r.id)}>
            Quick view
          </Button>
          <Link
            to={`${props.match.url}/${r.id}`.replace('//', '/')}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </Link>
        </Space>
      )
    }
  ]

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 4 }}>Customers {meta && `(${meta.count})`}</h3>
          <p style={{ color: '#888', marginBottom: 16 }}>
            Everyone who has used the WhatsApp duty service, with their activity at a glance.
            Sort by requests to spot high-volume users worth a dealer incentive.
          </p>

          {/* Filter bar */}
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search
              allowClear
              placeholder="Search phone, @username or MoMo name"
              style={{ width: 240 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onSearch={applyFilters}
            />
            <Select
              style={{ width: 200 }}
              value={ordering}
              onChange={setOrdering}
              options={ORDER_OPTIONS}
            />
            <Space size={6}>
              <Switch checked={dealersOnly} onChange={setDealersOnly} />
              <span>Dealers only</span>
            </Space>
            <Button type="primary" onClick={applyFilters}>
              Apply
            </Button>
            <Button onClick={clearFilters}>Clear</Button>
            <Button icon={<ReloadOutlined />} onClick={applyFilters}>
              Refresh
            </Button>
          </Space>

          <Table
            rowKey="id"
            dataSource={customers}
            columns={columns}
            scroll={{ x: 900 }}
            pagination={false}
            locale={{ emptyText: <Empty description="No customers yet" /> }}
          />
          {customers.length ? (
            <PaginationContainer
              meta={meta}
              getData={url => getCustomers({ search, dealersOnly, ordering }, url)}
            />
          ) : null}
        </div>
      </div>

      {/* Quick-view modal — same body as the full-page detail. */}
      <Modal
        open={!!quickViewId}
        onCancel={() => setQuickViewId(null)}
        footer={null}
        width={880}
        title="Customer detail"
        destroyOnClose
      >
        {quickViewId ? <CustomerDetailBody id={quickViewId} /> : null}
      </Modal>
    </PageLoader>
  )
}

CustomersList.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object
}

const CustomersRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={CustomersList} />
    <PrivateRoute exact path={`${props.match.path}/:id`} component={CustomerDetail} />
  </RouterSwitch>
)

CustomersRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(CustomersRoutes)
